import React from 'react';
import { SerializedStyles, css } from '@emotion/react';
import { useTheme } from '@veneer/theme';
import { focusRingStyle } from '../../../utils/focus_ring';
import getPillBorder from '../../../utils/get_pill_border';

/**
 *
 * @returns container styles for input
 *
 * background
 * border
 * error
 * foreground
 * transition
 * :disabled
 * :focus
 * :hover
 * ::placeholder
 */

const useInputStyles = ({
  leadingWidth,
  trailingWidth,
}: {
  leadingWidth?: number;
  trailingWidth?: number;
}): {
  root: {
    base: SerializedStyles;
    helperTextVisible: SerializedStyles;
  };
  input: {
    base: SerializedStyles;
    paddings: {
      base: SerializedStyles;
      separateLabel: SerializedStyles;
      pill: SerializedStyles;
    };
    trailingArea: {
      base: SerializedStyles;
      pill: SerializedStyles;
    };
    icon: {
      container: SerializedStyles;
      base: SerializedStyles;
      disabled: SerializedStyles;
      pointer: SerializedStyles;
    };
    label: {
      base: SerializedStyles;
      pill: SerializedStyles;
    };

    prefix: {
      base: SerializedStyles;
      pill: SerializedStyles;
    };
    suffix: SerializedStyles;
    hc: SerializedStyles;
  };
} => {
  const theme = useTheme();
  const hasLeadingArea = Boolean(leadingWidth && leadingWidth > 0);
  const hasTrailingArea = Boolean(trailingWidth && trailingWidth > 0);

  const paddingWithItem = `calc(${theme.orbitalIntermediate} - ${theme.layout.size1})`;

  const leadingArea = `calc(${leadingWidth}px + ${theme.orbitalThree})`;
  const trailingArea = `calc(${trailingWidth}px + ${theme.orbitalThree})`;

  return React.useMemo(
    () => ({
      root: {
        base: css({
          display: 'flex',
          flexDirection: 'column',
          gap: theme.layout.size1,
          minWidth: '144px',
        }),
        helperTextVisible: css({
          ':focus-within .vn-helper-text': { display: 'flex' },
        }),
      },

      input: {
        base: css({
          backgroundColor: theme.color.background.container.default,
          border: `1px solid ${theme.color.neutral.base.default}`,
          borderRadius: theme.borderRadius,
          boxSizing: 'border-box',

          width: '100%',
          minWidth: '144px',

          color: theme.color.foreground.default,
          ...theme.typography.labelRegular,
          lineHeight: '24px',
          [`@media (min-width: ${theme.layout.smMin})`]: { lineHeight: '24px' },
          [`@media (min-width: ${theme.layout.lgMin})`]: { lineHeight: '24px' },

          transition:
            'background-color 150ms ease, border-color 150ms ease, color 150ms ease',

          '&::placeholder': {
            color: theme.color.foreground.light,
            transition: 'opacity 150ms ease',
          },

          '&:hover': {
            borderColor: theme.color.neutral.base.hover,
            backgroundColor: theme.color.background.container.hover,
          },

          '&:focus': {
            ...focusRingStyle({
              theme,
              outlineOffset: -2,
              shadowSize: 0,
            }),
            outlineColor: theme.color.primary.base.default,
            '::placeholder': {
              opacity: 1,
            },
            '&:hover': {
              backgroundColor: theme.color.background.container.default,
              border: `1px solid ${theme.color.neutral.base.default}`,
            },
          },

          '&[aria-invalid="true"]': {
            borderColor: theme.color.negative.base.default,
            '&:hover': { borderColor: theme.color.negative.base.hover },
          },

          '&:disabled': {
            backgroundColor: theme.color.background.disabled,
            borderColor: theme.color.foreground.disabled,
            color: theme.color.foreground.disabled,
            pointerEvents: 'none',
            '&::placeholder': {
              color: theme.color.foreground.disabled,
              opacity: 1,
            },
          },
        }),

        paddings: {
          base: css({
            paddingBlock: `calc(${theme.orbitalIntermediate} - 1px)`,
            paddingInlineStart: hasLeadingArea
              ? `calc(${leadingArea} + ${paddingWithItem} - 1px)`
              : `calc(${theme.orbitalThree} - 1px)`,

            paddingInlineEnd: hasTrailingArea
              ? `calc(${trailingArea} + ${paddingWithItem} - 1px)`
              : `calc(${theme.orbitalThree} - 1px)`,
          }),
          separateLabel: css({
            paddingInlineStart: hasLeadingArea
              ? `calc(${leadingArea} + ${paddingWithItem} - 1px)`
              : `calc(${theme.orbitalFour} - 1px)`,

            paddingInlineEnd: hasTrailingArea
              ? `calc(${trailingArea} + ${paddingWithItem} - 1px)`
              : `calc(${theme.orbitalFour} - 1px)`,
          }),
          pill: css({
            borderRadius: getPillBorder,
            paddingInlineStart: hasLeadingArea
              ? `calc(${leadingArea} + ${theme.orbitalThree} - 1px)`
              : `calc(${theme.orbitalFour} - 1px)`,

            paddingInlineEnd: hasTrailingArea
              ? `calc(${trailingArea} + ${theme.orbitalThree} - 1px)`
              : `calc(${theme.orbitalFour} - 1px)`,
          }),
        },

        label: {
          base: css({
            paddingInlineStart: hasLeadingArea
              ? `calc(${leadingArea} + ${paddingWithItem})`
              : `calc(${theme.orbitalThree})`,
            paddingInlineEnd: hasTrailingArea
              ? `calc(${trailingArea} + ${paddingWithItem})`
              : `calc(${theme.orbitalThree})`,
          }),
          pill: css({
            paddingInlineStart: hasLeadingArea
              ? `calc(${leadingArea} + ${theme.orbitalThree})`
              : `calc(${theme.orbitalFour})`,
            paddingInlineEnd: hasTrailingArea
              ? `calc(${trailingArea} + ${theme.orbitalThree})`
              : `calc(${theme.orbitalFour})`,
          }),
        },

        // This is for the trailingArea (icon, button and area)
        trailingArea: {
          base: css({
            alignItems: 'center',
            alignSelf: 'center',
            display: 'flex',
            justifyContent: 'center',
            position: 'absolute',
            gap: theme.orbitalTwo,
            right: paddingWithItem,
          }),
          pill: css({
            right: theme.orbitalThree,
          }),
        },

        icon: {
          container: css({
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
            minHeight: '32px',
            minWidth: '32px',
          }),
          base: css({
            minWidth: '32px',
            pointerEvents: 'none',
            fill: theme.color.foreground.light,
          }),
          disabled: css({
            pointerEvents: 'none',
            fill: theme.color.foreground.disabled,
          }),
          pointer: css({
            cursor: 'pointer',
          }),
        },

        prefix: {
          base: css({
            alignItems: 'center',
            alignSelf: 'center',
            color: theme.color.foreground.light,
            display: 'flex',
            justifyContent: 'center',
            position: 'absolute',
            left: paddingWithItem,
          }),
          pill: css({
            left: theme.orbitalThree,
          }),
        },

        suffix: css({
          color: theme.color.foreground.light,
          display: 'flex',
        }),

        hc: css({
          '&:focus': {
            outlineColor: theme.color.primary.base.active,
          },
        }),
      },
    }),
    [
      hasLeadingArea,
      hasTrailingArea,
      leadingArea,
      paddingWithItem,
      theme,
      trailingArea,
    ]
  );
};

export default useInputStyles;
